import React, { useMemo, useState } from 'react';
import { FiChevronRight, FiHome, FiList, FiMapPin } from 'react-icons/fi';
import { EmptyState, RowSkeleton } from './states';
import { PMS_LABELS, fmtMoney, fmtNumber } from './derive';
import { coverageStatus, rankGaps } from './coverage';
import { stateName } from './usStates';
import { STATUS_COLORS } from './views/CanvasOrders';

// Right-hand panel whose content follows the active tab, so the dashboard
// never shows a block of unrelated lists next to the main chart.

const formatRelative = (date) => {
  if (!date) return '—';
  const ms = Date.now() - new Date(date).getTime();
  if (!Number.isFinite(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const TONE_CLASSES = {
  red: 'bg-rose-50 text-rose-700 ring-rose-200',
  amber: 'bg-orange-50 text-orange-700 ring-orange-200',
  emerald: 'bg-teal-50 text-emerald-700 ring-teal-200',
  sky: 'bg-slate-100 text-slate-700 ring-sky-200',
  zinc: 'bg-zinc-100 text-zinc-600 ring-zinc-200',
};

function Panel({ title, meta, children, action }) {
  return (
    <section className="flex min-h-0 flex-col">
      <div className="flex items-center justify-between px-3 pb-1.5 pt-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{title}</p>
        <div className="flex items-center gap-2">
          {meta ? <span className="text-[10px] tabular-nums text-zinc-400">{meta}</span> : null}
          {action}
        </div>
      </div>
      {children}
    </section>
  );
}

function RowButton({ onClick, onMouseEnter, onMouseLeave, active, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`group flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors duration-100 motion-reduce:transition-none ${
        active ? 'bg-zinc-100' : 'hover:bg-zinc-50'
      }`}
    >
      {children}
    </button>
  );
}

function CoverageGaps({ coverage, loading, focusState, onFocusState, highlightId, onHighlight }) {
  const [mode, setMode] = useState('states');
  const rows = useMemo(() => {
    const source = mode === 'states' ? coverage?.states || [] : coverage?.cities || [];
    const scoped = focusState && mode === 'cities' ? source.filter((row) => row.state === focusState) : source;
    return rankGaps(scoped, { limit: 30 });
  }, [coverage, mode, focusState]);

  if (loading && !coverage) return <RowSkeleton rows={8} />;

  return (
    <Panel
      title="Needs drivers"
      meta={rows.length ? `${rows.length} ranked` : null}
      action={
        <div className="flex items-center rounded-md bg-zinc-100 p-0.5">
          {[
            { id: 'states', label: 'States' },
            { id: 'cities', label: 'Cities' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setMode(opt.id)}
              className={`h-6 rounded px-2 text-[10.5px] font-medium ${
                mode === opt.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      }
    >
      <p className="px-3 pb-2 text-[10.5px] text-zinc-500">
        Ranked by in-range orders per active driver. Red rows have demand but nobody who can take an order.
      </p>
      {rows.length === 0 ? (
        <EmptyState title="No gaps found" description="Every area with demand has active drivers." icon={FiMapPin} />
      ) : (
        <ul className="divide-y divide-zinc-100">
          {rows.map((row) => {
            const id = mode === 'states' ? row.state : row.id;
            const status = coverageStatus(row);
            const title = mode === 'states' ? stateName(row.state) : row.city;
            const subtitle = mode === 'states' ? `${fmtNumber(row.cities || 0)} cities` : row.state;
            return (
              <li key={id}>
                <RowButton
                  active={highlightId === id || (mode === 'states' && focusState === row.state)}
                  onClick={() => onFocusState?.(focusState === row.state && mode === 'states' ? null : row.state)}
                  onMouseEnter={() => onHighlight?.(id)}
                  onMouseLeave={() => onHighlight?.(null)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate text-[12px] font-semibold text-zinc-900">{title}</p>
                      <span className={`rounded px-1 py-0.5 text-[9px] font-semibold ring-1 ${TONE_CLASSES[status.tone]}`}>{status.label}</span>
                    </div>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {subtitle} · {fmtNumber(row.properties || 0)} props
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[12px] font-semibold tabular-nums text-zinc-900">
                      {fmtNumber(row.orders || 0)} <span className="text-[10px] font-medium text-zinc-500">orders</span>
                    </p>
                    <p className="text-[10.5px] tabular-nums text-zinc-500">
                      {fmtNumber(row.activeDrivers || 0)} active / {fmtNumber(row.drivers || 0)} drivers
                    </p>
                  </div>
                </RowButton>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

function OrdersContext({ summary, onSelectHost }) {
  const statusMix = summary?.breakdowns?.orderStatus || [];
  const topHosts = summary?.leaders?.topHosts || [];
  const total = statusMix.reduce((sum, row) => sum + (row.count || 0), 0);
  return (
    <>
      <Panel title="Status mix" meta={total ? fmtNumber(total) : null}>
        {statusMix.length === 0 ? (
          <p className="px-3 pb-3 text-[11px] text-zinc-500">No orders in scope.</p>
        ) : (
          <div className="px-3 pb-3">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-zinc-100">
              {statusMix.map((row) => (
                <div
                  key={row._id}
                  title={`${row._id}: ${row.count}`}
                  className="h-full"
                  style={{ width: `${total ? (row.count / total) * 100 : 0}%`, background: STATUS_COLORS[row._id] || '#a1a1aa' }}
                />
              ))}
            </div>
            <ul className="mt-2 space-y-1">
              {statusMix.map((row) => (
                <li key={row._id} className="flex items-center justify-between text-[11px]">
                  <span className="inline-flex items-center gap-1.5 text-zinc-700">
                    <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLORS[row._id] || '#a1a1aa' }} />
                    {row._id}
                  </span>
                  <span className="tabular-nums text-zinc-500">
                    {fmtNumber(row.count)} · {total ? Math.round((row.count / total) * 100) : 0}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Panel>
      <Panel title="Top hosts by orders" meta={topHosts.length || null}>
        {topHosts.length === 0 ? (
          <p className="px-3 pb-3 text-[11px] text-zinc-500">No host orders in scope.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {topHosts.map((host, index) => (
              <li key={host._id}>
                <RowButton onClick={() => onSelectHost?.(host)}>
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">{index + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">{host.name}</p>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {fmtNumber(host.orderCount || 0)} orders · {fmtMoney(host.revenue || 0)}
                    </p>
                  </div>
                  <FiChevronRight className="h-3 w-3 text-zinc-300 group-hover:text-zinc-500" />
                </RowButton>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}

function HostsContext({ summary, onSelectHost, onSelectPms }) {
  const pmsRows = summary?.breakdowns?.hostPms || [];
  const recent = summary?.activity?.hosts || [];
  const pmsTotal = pmsRows.reduce((sum, row) => sum + (row.count || 0), 0);
  const max = pmsRows.reduce((m, row) => Math.max(m, row.count || 0), 0);
  return (
    <>
      <Panel title="Hosts by PMS" meta={pmsTotal ? fmtNumber(pmsTotal) : null}>
        {pmsRows.length === 0 ? (
          <p className="px-3 pb-3 text-[11px] text-zinc-500">No hosts in scope.</p>
        ) : (
          <ul className="space-y-1.5 px-3 pb-3">
            {pmsRows.map((row) => (
              <li key={row._id}>
                <button type="button" onClick={() => onSelectPms?.(row._id)} className="group w-full text-left">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-medium text-zinc-800 group-hover:text-zinc-900">{PMS_LABELS[row._id] || row._id}</span>
                    <span className="tabular-nums text-zinc-500">
                      {fmtNumber(row.count)} · {pmsTotal ? Math.round((row.count / pmsTotal) * 100) : 0}%
                    </span>
                  </div>
                  <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                    <div className="h-full rounded-full bg-indigo-500" style={{ width: `${max ? (row.count / max) * 100 : 0}%` }} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="Recent hosts" meta={recent.length || null}>
        {recent.length === 0 ? (
          <p className="px-3 pb-3 text-[11px] text-zinc-500">No new hosts in range.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {recent.slice(0, 12).map((host) => (
              <li key={host._id}>
                <RowButton onClick={() => onSelectHost?.(host)}>
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-[10px] font-semibold text-indigo-700">
                    {(host.name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">{host.name}</p>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {PMS_LABELS[host.pmsType] || 'Unknown PMS'} · {host.propertyCount || 0} props
                    </p>
                  </div>
                  <span className="text-[10px] tabular-nums text-zinc-400">{formatRelative(host.createdAt)}</span>
                </RowButton>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}

function PropertiesContext({ summary, coverage, onSelectProperty }) {
  const recent = summary?.activity?.properties || [];
  const markets = useMemo(
    () => [...(coverage?.cities || [])].filter((c) => c.properties > 0).sort((a, b) => b.properties - a.properties).slice(0, 8),
    [coverage]
  );
  return (
    <>
      <Panel title="Top markets by properties" meta={markets.length || null}>
        {markets.length === 0 ? (
          <p className="px-3 pb-3 text-[11px] text-zinc-500">No market mix yet.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {markets.map((market) => (
              <li key={market.id} className="flex items-center justify-between px-3 py-1.5 text-[11px]">
                <span className="font-semibold text-zinc-900">
                  {market.city}, {market.state}
                </span>
                <span className="tabular-nums text-zinc-500">
                  {fmtNumber(market.properties)} props · {fmtNumber(market.activeDrivers)} active drivers
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="Recent properties" meta={recent.length || null}>
        {recent.length === 0 ? (
          <p className="px-3 pb-3 text-[11px] text-zinc-500">No new properties in range.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {recent.slice(0, 12).map((property) => (
              <li key={property._id}>
                <RowButton onClick={() => onSelectProperty?.(property)}>
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                    <FiHome className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">
                      {property?.address?.line1 || property?.listingTitle || 'Property'}
                    </p>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {property?.address?.city || ''}
                      {property?.address?.state ? `, ${property.address.state}` : ''}
                      {property.hostName ? ` · ${property.hostName}` : ''}
                    </p>
                  </div>
                  <span className="text-[10px] tabular-nums text-zinc-400">{formatRelative(property.createdAt)}</span>
                </RowButton>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}

function DriversContext({ summary, coverage, onFocusState, onChangeView }) {
  const windows = summary?.driverWindows || {};
  const topCities = useMemo(
    () => [...(coverage?.cities || [])].filter((c) => c.drivers > 0).sort((a, b) => b.drivers - a.drivers).slice(0, 10),
    [coverage]
  );
  return (
    <>
      <Panel title="Acquisition windows">
        <div className="grid grid-cols-2 gap-2 px-3 pb-3">
          {[
            { id: 'wtd', label: 'This week', value: windows.wtd },
            { id: 'mtd', label: 'This month', value: windows.mtd },
            { id: 'ytd', label: 'This year', value: windows.ytd },
            { id: 'total', label: 'All time', value: windows.total },
          ].map((w) => (
            <div key={w.id} className="rounded-lg border border-zinc-200 px-2.5 py-2">
              <p className="text-[9.5px] font-semibold uppercase tracking-wider text-zinc-500">{w.label}</p>
              <p className="mt-0.5 text-[15px] font-semibold tabular-nums text-zinc-900">{fmtNumber(w.value || 0)}</p>
            </div>
          ))}
        </div>
      </Panel>
      <Panel
        title="Top driver cities"
        meta={topCities.length || null}
        action={
          <button type="button" onClick={() => onChangeView?.('map')} className="inline-flex items-center gap-1 text-[10.5px] font-medium text-zinc-600 hover:text-zinc-900">
            <FiMapPin className="h-3 w-3" /> Map
          </button>
        }
      >
        {topCities.length === 0 ? (
          <p className="px-3 pb-3 text-[11px] text-zinc-500">No driver addresses on file.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {topCities.map((city) => (
              <li key={city.id}>
                <RowButton
                  onClick={() => {
                    onFocusState?.(city.state);
                    onChangeView?.('map');
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">
                      {city.city}, {city.state}
                    </p>
                    <p className="truncate text-[10.5px] text-zinc-500">{fmtNumber(city.orders || 0)} orders in range</p>
                  </div>
                  <span className="text-[12px] font-semibold tabular-nums text-zinc-900">
                    {fmtNumber(city.activeDrivers)}
                    <span className="text-[10px] font-medium text-zinc-500"> / {fmtNumber(city.drivers)}</span>
                  </span>
                </RowButton>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}

export default function ContextPanel({
  activeView,
  summary,
  coverage,
  coverageLoading,
  focusState,
  onFocusState,
  highlightId,
  onHighlight,
  onSelectHost,
  onSelectProperty,
  onSelectPms,
  onChangeView,
  onOpenRecords,
  recordsAvailable,
}) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div key={activeView} className="flex-1 overflow-y-auto animate-[fadeInPanel_150ms_ease-out_both] motion-reduce:animate-none">
        {activeView === 'map' ? (
          <CoverageGaps
            coverage={coverage}
            loading={coverageLoading}
            focusState={focusState}
            onFocusState={onFocusState}
            highlightId={highlightId}
            onHighlight={onHighlight}
          />
        ) : activeView === 'orders' ? (
          <OrdersContext summary={summary} onSelectHost={onSelectHost} />
        ) : activeView === 'hosts' ? (
          <HostsContext summary={summary} onSelectHost={onSelectHost} onSelectPms={onSelectPms} />
        ) : activeView === 'properties' ? (
          <PropertiesContext summary={summary} coverage={coverage} onSelectProperty={onSelectProperty} />
        ) : (
          <DriversContext summary={summary} coverage={coverage} onFocusState={onFocusState} onChangeView={onChangeView} />
        )}
      </div>
      {recordsAvailable ? (
        <div className="border-t border-zinc-100 p-2">
          <button
            type="button"
            onClick={() => onOpenRecords?.(activeView)}
            className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-200 bg-white text-[11px] font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <FiList className="h-3.5 w-3.5" />
            Browse all records
          </button>
        </div>
      ) : null}
      <style>{`
        @keyframes fadeInPanel {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
